'use client';

import Link from 'next/link';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { ReactComponent as SvgLogoSnow } from 'public/assets/white-logo.svg';
import { ReactComponent as SvgLogoSki } from 'public/assets/white-logo_ski.svg';
import { ReactComponent as SvgLogoMountainNewsSummer } from 'public/assets/mountain-news-summer.svg';
import { ReactComponent as SvgLogoMountainGetawaySummer } from 'public/assets/mountain-getaway-summer.svg';
import { Search, Menu } from 'lucide-react';
import { HeaderNavCurLink } from '@/components/common/HeaderNav/types';

import { REVIEW_ANCHOR } from '@/page-components/resort/constants';

import { cn } from '@/util/cn';
import { Routes } from '@/routes';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { SimpleLangSelect } from '@/components/resort/HeaderLangSelect/HeaderLangSelect';

import { useAnalytics } from '@/tracking/analytics';
import { getPortalOptions } from '@/config/getPortalConfig';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import { isMainLocale } from '@/util/i18nFlags';
import { containerClass, Container } from '@/components/ui/container';

import { IS_SSR } from '@/ots-constants';
import { setFocusToSearchInput } from '@/util/setFocusToInputByQuery';
import { useNoHeader } from '@/util/useNoHeader';
import { useDefaultScrollToTop } from '@/util/useScrollToTop';

import { useSettingsPrefetch } from '@/settings/SettingsContext';
import { useShouldAdjustForSummer } from '@/util/useShouldAdjustForSummer';
import { getSummerLogoHrefUrl } from '@/util/getSummerLogoHrefUrl';
import { SummerHeader } from './SummerHeader';

// HeaderSearch (desktop inline typeahead) and SearchPopup (mobile +
// fallback full-screen overlay) are both gated on rare user actions
// (typing or tapping the search icon). Lazy-load both to keep them
// out of the initial layout chunk.
const HeaderSearch = dynamic(
  () => import('@/components/common/HeaderSearch/HeaderSearch').then((m) => m.HeaderSearch),
  { ssr: false, loading: () => null },
);
const SearchPopup = dynamic(
  () => import('@/components/home/SearchPopup').then((m) => m.SearchPopup),
  { ssr: false, loading: () => null },
);

// Re-exported for back-compat with existing consumers that imported the
// enum from this file. New code (especially server-side) should import
// from `@/components/common/HeaderNav/types` directly.
export { HeaderNavCurLink } from '@/components/common/HeaderNav/types';

export type HeaderNavProps = {
  current: HeaderNavCurLink;
  isOnSummerOverview?: boolean;
};

// Summer-overview palette swap (winter brand-blue → summer green).
// Hex literals match $primary-green / $primary-hover-summer / $primary-alt-summer
// from src/styles/variables.scss until Tier 7 collapses them into semantic tokens.
const winterPalette = {
  bg: 'bg-primary',
  hoverBg: 'hover:bg-primary-hover',
  currentBg: 'bg-primary-hover',
  mobileActive: 'text-[#11ABD8]',
};

const summerPalette = {
  bg: 'bg-primary-summer',
  hoverBg: 'hover:bg-primary-summer-hover',
  currentBg: 'bg-primary-summer-hover',
  mobileActive: 'text-[#78C884]',
};

// Desktop link sits inside a flex row with gap-x-1 between siblings.
// Tracking adds the wide-letter feel of the legacy chrome at a smaller
// font size; transition softens the bg color swap on hover/active.
const desktopLinkClass = [
  'flex h-full items-center rounded-sm px-3 text-xs font-medium uppercase tracking-wider leading-none',
  'text-white/90 no-underline transition-colors',
  'hover:text-white',
  'max-md:hidden',
].join(' ');

const mobileLinkClass = [
  'text-[1.7rem] uppercase leading-[5.1rem] text-[#005889] no-underline',
  'hover:underline',
].join(' ');

const mobileIconButtonClass = [
  'flex h-10 w-10 cursor-pointer items-center justify-center text-white',
  'md:hidden',
].join(' ');

export const HeaderNav = ({
  current,
  isOnSummerOverview,
}: HeaderNavProps) => {
  const prefetch = useSettingsPrefetch();
  const { t } = useOTSTranslation('common');
  const [isMenuOpen, setMenuOpen] = useState<boolean>(false);
  const [isSearchOpen, setSearchOpen] = useState<boolean>(false);
  const a = useAnalytics();

  const shouldAdjustForSummer = useShouldAdjustForSummer();
  const palette = isOnSummerOverview ? summerPalette : winterPalette;

  const divRef = useRef<HTMLDivElement>(null);

  const logoHrefUrl = getSummerLogoHrefUrl(isOnSummerOverview || false);

  const toggleMenu = useCallback(() => {
    document.body.classList.toggle('search-overlay');
    setMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);

  const toggleSearch = useCallback(() => {
    new Promise<void>((resolve) => {
      setSearchOpen(!isSearchOpen);
      if (isSearchOpen) {
        a.searchBox();
      }
      resolve();
    }).then(() => {
      setFocusToSearchInput();
    }).catch(() => {});
  }, [isSearchOpen]);

  useEffect(() => () => document.body.classList.remove('search-overlay'), []);

  const hasMagazine = getPortalOptions().hasNews;

  const showUSLogo = [ELocale.EN_US, ELocale.EN_GB, ELocale.CS_CZ, ELocale.SK_SK].includes(getLocale() as ELocale);
  const showMountainGetawaySummer = isOnSummerOverview && getLocale() === ELocale.EN_US;
  const showMountainNewsSummer = isOnSummerOverview
    && [ELocale.FR_FR, ELocale.DE_DE, ELocale.IT_IT, ELocale.EN_GB, ELocale.PL_PL].includes(getLocale() as ELocale);
  const showSkiGPT = [ELocale.EN_US, ELocale.EN_GB].includes(getLocale() as ELocale);

  const renderLogo = () => {
    if (showMountainGetawaySummer) return <SvgLogoMountainGetawaySummer />;
    if (showMountainNewsSummer) return <SvgLogoMountainNewsSummer />;
    return showUSLogo ? <SvgLogoSnow /> : <SvgLogoSki />;
  };

  const logoRef = useRef<HTMLAnchorElement>(null);
  const pathname = usePathname() ?? '';
  // usePathname() drops the URL fragment, but the legacy refocus-after-
  // OneTrust-close behavior keyed off whether the user was already on the
  // reviews anchor. Fall back to window.location.hash for that bit.
  const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';

  useDefaultScrollToTop(0, 0);

  const refocusToRatingBlock = useCallback((e: FocusEvent) => {
    const relatedTarget = e?.relatedTarget as HTMLElement | null;

    if (relatedTarget?.id && relatedTarget.id.indexOf('onetrust') >= 0
      && !IS_SSR
      && hash === REVIEW_ANCHOR) {
      const scrollableBlock = document.getElementById(REVIEW_ANCHOR);

      scrollableBlock?.scrollIntoView();
    }
  }, [hash]);

  useEffect(() => {
    logoRef?.current?.addEventListener('focus', refocusToRatingBlock);
    return () => logoRef?.current?.removeEventListener('focus', refocusToRatingBlock);
  }, []);

  const closeSearch = useCallback(() => setSearchOpen(false), [setSearchOpen]);

  // App Router has no router.events; close the search popup whenever the
  // pathname changes (pages-router-style routeChangeComplete equivalent).
  useEffect(() => {
    closeSearch();
  }, [pathname, closeSearch]);

  const { noHeader } = useNoHeader();
  if (noHeader) {
    return null;
  }

  if (shouldAdjustForSummer) {
    return <SummerHeader current={current} isOnSummerOverview={isOnSummerOverview} />;
  }

  return (
    <>
      {isSearchOpen && <SearchPopup closePopup={toggleSearch} />}
      <nav ref={divRef} className={cn('z-[12] h-10 md:h-[46px]', palette.bg)}>
        <Container width="xl-only" className="flex h-10 items-center justify-between gap-x-4 md:h-[46px]">
          <div className="flex h-10 items-center gap-x-6 md:h-[46px]">
            <Link
              scroll={false}
              href={logoHrefUrl}
              prefetch={prefetch}
              ref={logoRef}
              tabIndex={-1}
              className="flex items-center [&_svg]:h-[22px] md:[&_svg]:h-[26px]"
            >
              {renderLogo()}
            </Link>
            <div className="flex h-full items-center gap-x-1 max-md:hidden">
              <Link
                scroll={false}
                href={Routes.snowReports()}
                prefetch={prefetch}
                className={cn(
                  desktopLinkClass,
                  palette.hoverBg,
                  current === HeaderNavCurLink.SNOW_REPORTS && cn('!text-white', palette.currentBg),
                )}
              >
                {t('nav.global.snowReports')}
              </Link>
              <Link
                scroll={false}
                href={Routes.tripPlanning()}
                prefetch={prefetch}
                className={cn(
                  desktopLinkClass,
                  palette.hoverBg,
                  current === HeaderNavCurLink.TRIP_PLANNING && cn('!text-white', palette.currentBg),
                )}
              >
                {t('nav.global.planning')}
              </Link>
              {hasMagazine && (
                <a
                  href={Routes.magazine}
                  className={cn(
                    desktopLinkClass,
                    palette.hoverBg,
                    current === HeaderNavCurLink.MAGAZINE && cn('!text-white', palette.currentBg),
                  )}
                >
                  {t('nav.global.magazine')}
                </a>
              )}
              {showSkiGPT && (
                <a
                  href="https://chat.onthesnow.com/"
                  className={cn(
                    desktopLinkClass,
                    'normal-case',
                    palette.hoverBg,
                    current === HeaderNavCurLink.SKIGPT && cn('!text-white', palette.currentBg),
                  )}
                >
                  SkiGPT
                </a>
              )}
            </div>
          </div>
          <div className="flex h-10 items-center gap-x-2 md:h-[46px]">
            <HeaderSearch className="max-md:hidden" />
            <div
              className={cn(
                'rounded bg-white/10 max-md:hidden',
                palette.hoverBg,
              )}
            >
              <SimpleLangSelect isOnSummerOverview={isOnSummerOverview} />
            </div>
            <div
              tabIndex={-1}
              role="button"
              className={mobileIconButtonClass}
              onClick={toggleSearch}
            >
              <Search size={20} strokeWidth={2.25} />
            </div>
            <div
              tabIndex={-1}
              role="button"
              className={mobileIconButtonClass}
              onClick={toggleMenu}
            >
              <Menu size={22} strokeWidth={2.25} />
            </div>
          </div>
        </Container>
        {/* Mobile menu overlay */}
        <div
          className={cn(
            'fixed inset-x-0 top-0 z-[12] flex flex-col items-center justify-center overflow-hidden bg-white opacity-0',
            'transition-[bottom,opacity] duration-200',
            isMenuOpen ? 'bottom-0 opacity-100' : 'bottom-full',
          )}
        >
          <div className="h-[52px] w-full">
            <div className={cn(containerClass, 'flex justify-end')}>
              <div
                tabIndex={-1}
                role="button"
                className="flex h-[52px] w-[52px] cursor-pointer items-center justify-center text-[#005889]"
                onClick={toggleMenu}
              >
                <Menu size={22} strokeWidth={2.25} />
              </div>
            </div>
          </div>
          <div className="flex w-full flex-1 flex-col items-center justify-center">
            <Link
              scroll={false}
              href={Routes.snowReports()}
              prefetch={prefetch}
              className={cn(
                mobileLinkClass,
                current === HeaderNavCurLink.SNOW_REPORTS && cn('font-semibold', palette.mobileActive),
              )}
            >

              {t('nav.global.snowReports')}

            </Link>
            {isMainLocale() && (
              <Link
                scroll={false}
                href={Routes.tripPlanning()}
                prefetch={prefetch}
                className={cn(
                  mobileLinkClass,
                  current === HeaderNavCurLink.TRIP_PLANNING && cn('font-semibold', palette.mobileActive),
                )}
              >

                {t('nav.global.planning')}

              </Link>
            )}
            {hasMagazine && (
              <a
                href={Routes.magazine}
                className={cn(
                  mobileLinkClass,
                  current === HeaderNavCurLink.MAGAZINE && cn('font-semibold', palette.mobileActive),
                )}
              >
                {t('nav.global.magazine')}
              </a>
            )}
            {showSkiGPT && (
              <a
                href="https://chat.onthesnow.com/"
                className={cn(
                  mobileLinkClass,
                  'normal-case',
                  current === HeaderNavCurLink.SKIGPT && cn('font-semibold', palette.mobileActive),
                )}
              >
                SkiGPT
              </a>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};
